import React, { useState } from "react";
import { Link } from "react-router-dom";
import { authApi } from "../../services/api/authApi";
import { showError, showSuccess } from "../../lib/toast";
import * as Yup from "yup";

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errors, setErrors] = useState<Partial<ForgotPasswordFormData>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Please enter a valid email address").required("Email is required"),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // validate field on change if it was touched
    if (touched[name]) {
      validateField(name as keyof ForgotPasswordFormData, value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name as keyof ForgotPasswordFormData, value);
  };

  const validateForm = (): boolean => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      const nextErrors: Partial<ForgotPasswordFormData> = {};
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e: any) => {
          if (!nextErrors[e.path as keyof ForgotPasswordFormData]) {
            nextErrors[e.path as keyof ForgotPasswordFormData] = e.message;
          }
        });
      }
      setErrors(nextErrors);
      return false;
    }
  };

  const validateField = (field: keyof ForgotPasswordFormData, value: string) => {
    try {
      validationSchema.validateSyncAt(field as string, { ...formData, [field]: value });
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, [field]: e.message }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApi.forgotPassword({
        email: formData.email,
      });

      setIsEmailSent(true);
      const successMessage = ((response as any)?.message) || ((response as any)?.data?.message) || "Password reset link has been sent to your email";
      showSuccess(successMessage);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send password reset email. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApi.forgotPassword({
        email: formData.email,
      });

      const successMessage = ((response as any)?.message) || ((response as any)?.data?.message) || "Password reset link has been sent again";
      showSuccess(successMessage);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to resend password reset email. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-vh-100 d-flex flex-column">
        {/* Breadcrumb */}
        <div className="py-3 font_two">
          <div className="container">
            <p className="text-muted small mb-0 font_two">
              <Link to="/" className="text-muted text-decoration-none">
                Home
              </Link>{" "}
              /<span className="fw-bold"> Forgot Password</span>
            </p>
          </div>
        </div>

        {/* Success Message */}
        <div className="container flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="col-md-6">
            <div className="card text-center">
              <div className="card-body p-5">
                <div className="text-success mb-4">
                  <i className="fas fa-envelope fa-4x"></i>
                </div>
                <h3 className="font_two color_darkblue mb-3">Check Your Email</h3>
                <p className="font_two text-muted mb-4">
                  We've sent a password reset link to <strong>{formData.email}</strong>
                </p>
                <p className="font_two text-muted mb-4">
                  Please check your email inbox and click the reset link to create a new password.
                  The link will expire in 30 minutes.
                </p>
                
                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                  <button
                    className="font_two color_darkblue btn btn-outline-dark"
                    onClick={handleResendEmail}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      "Resend Email"
                    )}
                  </button>
                  <Link to="/login" className="font_two btn btn-dark"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Breadcrumb */}
      <div className="py-3 font_two">
        <div className="container">
          <p className="text-muted small mb-0 font_two">
            <Link to="/" className="text-muted text-decoration-none">
              Home
            </Link>{" "}
            /<span className="fw-bold"> Forgot Password</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row py-4">
        {/* Left Content */}
        <div className="col-md-6 mb-4">
          <h4 className="display-6 mb-3">Forgot Password?</h4>
          <p className="text-muted font_two mb-3">
            Don't worry! Enter your email address and we'll send you a link to reset your password.
          </p>
          <Link to="/login" className="text-link" style={{ textUnderlinePosition: 'from-font'}}>
            Remember your password? Sign in
          </Link>
        </div>

        {/* Right Form */}
        <div className="col-md-6">
          <form
            onSubmit={handleSubmit}
            className="bg-white p-4 rounded-3 shadow-sm"
          >
            <div className="mb-4">
              <label htmlFor="email" className="form-label fw-medium">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                // required
                className="form-control form-control-sm custom-input"
              />
              {errors.email && (
                <small className="font_two text-danger d-block mt-1">{errors.email}</small>
              )}
            </div>

            <div className="text-center pt-2">
              <button
                type="submit"
                className="btn btn-dark primary-btn w-50 rounded-pill"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Sending Reset Link...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
